import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '@bliks-io/ui';
import { useAuthStore } from '@/stores/auth-store';
import { STEP_ACTION_ICONS } from '@/components/processes/step-action-icons';
import type { DashboardProcess } from '@bliks-io/contracts';

interface DashboardProcessListProps {
  processes: DashboardProcess[];
}

export function DashboardProcessList({ processes }: DashboardProcessListProps) {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(
    () => [...processes].sort((a, b) => b.activities.length - a.activities.length),
    [processes]
  );
  const visibleProcesses = showAll ? sorted : sorted.slice(0, 3);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="text-lg">{t('dashboard.processes.title')}</CardTitle>
        {sorted.length > 3 && (
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-sm text-primary hover:underline"
          >
            {showAll ? t('common.showLess') : t('common.viewAll')}
          </button>
        )}
      </CardHeader>
      <CardContent>
        {sorted.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dashboard.processes.noProcesses')}</p>
        ) : (
          <div className="space-y-1">
            {visibleProcesses.map((process) => (
              <div key={process._id} className="rounded-md border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between p-3 text-left hover:bg-muted/50"
                  onClick={() => setExpandedId(expandedId === process._id ? null : process._id)}
                >
                  <div className="min-w-0 flex-1">
                    <Link
                      to="/$slug/processes/all-processes/$processId"
                      params={{ slug, processId: process._id }}
                      className="truncate text-sm font-medium hover:underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {process.name}
                    </Link>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {t('dashboard.processes.activities', {
                        count: process.activities.length
                      })}
                    </p>
                  </div>
                  {expandedId === process._id ? (
                    <ChevronUp className="h-4 w-4 shrink-0 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
                  )}
                </button>
                {expandedId === process._id && process.activities.length > 0 && (
                  <div className="border-t px-3 py-2">
                    <ul className="space-y-1">
                      {process.activities.map((activity) => {
                        const Icon = STEP_ACTION_ICONS[activity.actionType];
                        const displayName =
                          activity.name === 'Unnamed'
                            ? t('dashboard.activities.unnamed', {
                                type: t(`processes.${activity.actionType}`)
                              })
                            : activity.name;
                        return (
                          <li key={activity._id} className="flex items-center gap-2 py-1 text-sm">
                            <Icon
                              className={`h-4 w-4 shrink-0 ${
                                activity.actionType === 'task' ? 'text-info' : 'text-highlight'
                              }`}
                            />
                            <span className="truncate">{displayName}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
